import { GetServerSideProps, NextPage } from 'next';
import { useEffect } from 'react';

import { EventsPageSeo } from '@/components/ui/features/events-app/event/EventsPageSeo/EventsPageSeo';
import { EventTicketRegister } from '@/components/ui/features/events-app/reception/EventTicketRegister/EventTicketRegister';
import { EventAppLayout } from '@/components/ui/shared/layouts/EventAppLayout/EventAppLayout';
import { NoTicketCheck } from '@/components/ui/shared/overlays/NoTicketCheck/NoTicketCheck';
import { ReceptionContent } from '@/containers/Reception/ReceptionContent';
import { useQueryParams } from '@/hooks/useQueryParams';
import { queryEventAnnouncements } from '@/queries/announcements';
import { queryThinEvent } from '@/queries/thin-events';
import { fetchEventConfiguration } from '@/queries/upgrade/eventConfiguration';
import { generateStore } from '@/utils/generateStore';
import { Hydrate, IHydrateProps } from '@/utils/swr/Hydrate';
import { temporaryRedirects } from '@/utils/temporaryRedirects';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const ReceptionPage: NextPage<IHydrateProps> = ({ fallback }) => {
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  // TODO: collocate this with component which renders calendar component
  // Check first question https://www.addevent.com/documentation/add-to-calendar-button#anchor-faq for clarification
  useEffect(() => {
    const id = setTimeout(() => {
      // @ts-ignore
      if (window.addeventatc) {
        // @ts-ignore
        window.addeventatc.refresh();
      }
    }, 200);

    return () => clearTimeout(id);
  });

  return (
    <Hydrate fallback={fallback}>
      <EventAppLayout>
        <EventsPageSeo page="reception" />
        <NoTicketCheck eventId={eventId}>
          <ReceptionContent eventTicketSlot={<EventTicketRegister eventId={eventId} />} />
        </NoTicketCheck>
      </EventAppLayout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { eventId } = urlQueryStringValidate<{ eventId: string }>(context.query, {
    eventId: StringParam,
  });

  if (eventId in temporaryRedirects) {
    return {
      redirect: {
        destination: `/events/${temporaryRedirects[eventId]}/reception`,
        permanent: true,
      },
    };
  }

  try {
    const eventConfiguration = await fetchEventConfiguration(eventId, context.req.headers.cookie);

    if (!eventConfiguration?.reception_page) {
      return {
        notFound: true,
      };
    }

    await Promise.all([
      store.fetchResource(queryThinEvent(eventId)),
      store.fetchResources(queryEventAnnouncements(eventId)),
    ]);
  } catch (err) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      fallback: store.fallback,
    },
  };
};

export default ReceptionPage;
